/**
 * kinEventWrite.ts — the ONE write that puts an event into Kin.
 *
 * Extracted from `SchedulePreview.insertKinEvents` so the "add an event by
 * hand" sheet (#1957, item 2) saves through the same path Snap-a-Schedule
 * does, rather than growing a second writer. That matters for more than
 * tidiness: what makes a saved event reach her Apple Calendar is the `events`
 * mirror row — `kin-calendar-feed` serves that table and nothing else — so a
 * second writer that forgot the mirror would save an event that never syncs,
 * and the failure would be invisible until she looked at her phone.
 *
 * ⚠️ WHAT THIS DOES NOT DO, on purpose:
 *
 *   - It does NOT write `schedule_imports`. That table drives the free tier's
 *     "3 imports a month" cap, and a manual one-event add is not an import.
 *     Logging one here would let three typed events lock her out of scanning a
 *     school calendar. SchedulePreview keeps that insert, and stays the only
 *     writer of it (pinned by schedulePreviewGooglePush.test.ts).
 *   - It does NOT push to Google or hand off to Apple. Those are choices she
 *     makes with buttons, owned by the caller.
 *   - It does NOT carry `end_date`. `imported_events` has no such column (only
 *     `events` does), so a multi-day row cannot round-trip through this path.
 *     The far-future/trip writer in TodayTab owns spans.
 *
 * ── #1966 (her item 10) — the reminder is STORED now ──────────────────────────
 *
 * It used to not be, and that was the whole defect. The picker's value lived in
 * React state for the length of one save and reached exactly two places from
 * there: Google's `reminders.overrides`, and a VALARM in the ONE-SHOT Apple
 * `.ics`. A plain "Save to Kin" discarded it, and `kin-calendar-feed` — the
 * SUBSCRIBED calendar — hard-coded `"none"`, so the calendar she actually
 * subscribed to had never carried a single alarm.
 *
 * `reminder_minutes` is written to BOTH tables because each feeds a different
 * Apple route: `events` is what the subscribed feed serves, `imported_events`
 * is what `schedule-ics` reads for the one-shot handoff.
 *
 * ⚠️ NULL IS NOT ZERO. Null means she chose no reminder; `0` means "at the time
 * of the event", which is a real option in the picker and a legitimate `PT0M`
 * alarm. Hence `?? null` below and never `|| null` — the latter would turn her
 * at-the-time choice into no reminder at all, silently.
 */

import { supabase } from "@/integrations/supabase/client";
import { reportError } from "./reportError";

/** One event to save, already normalized: times are canonical "HH:MM:00" (or
 *  null), never a display string like "3:00 PM". Callers normalize with
 *  `displayTimeTo24Hour` so there is exactly one parser in the codebase. */
export interface KinEventDraft {
  title: string;
  /** "YYYY-MM-DD", local. */
  event_date: string;
  start_time: string | null;
  end_time: string | null;
  is_all_day: boolean;
  location: string | null;
  notes: string | null;
  /** The kid this belongs to, by name — the field the calendar colors by.
   *  null means family/unassigned and renders in periwinkle. */
  child: string | null;
}

export interface WriteKinEventsInput {
  userId: string;
  drafts: KinEventDraft[];
  /** Stamped on both rows: "schedule" | "newsletter" | "work_calendar" |
   *  "manual_add". "work_calendar" is ALSO what makes an event render in the
   *  legend's work slate, so it is a color input, not just provenance. */
  sourceType: string;
  /** Groups the rows of one save, so the Apple .ics handoff can address them. */
  batchId: string;
  /**
   * Minutes BEFORE the event to alert, or null for no reminder — #1966.
   *
   * Batch-level rather than per-draft because both callers have exactly one
   * picker per save: the import card asks once for the whole batch, and the
   * add-event sheet is one event. The COLUMN is per-row, so a future per-event
   * editor can diverge them without changing this signature.
   *
   * ⚠️ `0` is a real value ("at the time of the event"), so this is
   * `number | null` and never falsy-checked.
   */
  reminderMinutes?: number | null;
}

export interface WriteKinEventsResult {
  /** The ids we generated, in draft order. Empty when nothing was written. */
  insertedIds: string[];
  /** Set when the imported_events insert failed — nothing was written. */
  error: unknown;
  /** Set when the rows landed but the `events` mirror did not. The event IS
   *  saved but will not show on her week or reach Apple, so a caller must not
   *  report a plain success. */
  mirrorError: unknown;
}

/**
 * Inserts each draft into `imported_events` and mirrors it into `events`.
 *
 * ⚠️ The ids are generated HERE and used for both rows (#1806). A multi-row
 * insert gives every row the same `created_at`, so insertion order cannot be
 * recovered afterwards by sorting — and the Google write-back used to do
 * exactly that, which could land a Google id on the wrong event. `source_id`
 * points at the id we chose, not at a position in the RETURNING list.
 */
export async function writeKinEvents(
  input: WriteKinEventsInput,
): Promise<WriteKinEventsResult> {
  const { userId, drafts, sourceType, batchId, reminderMinutes } = input;
  if (!userId || drafts.length === 0) {
    return { insertedIds: [], error: null, mirrorError: null };
  }

  const rows = drafts.map((draft) => ({
    id: crypto.randomUUID(),
    user_id: userId,
    title: draft.title,
    event_date: draft.event_date,
    start_time: draft.start_time,
    end_time: draft.end_time,
    // #607 — an unparseable/absent time is TBD, NOT all-day. Only an explicit
    // all-day marker means all-day, so the "What time?" Quick Question that
    // exists to recover an unknown time still fires.
    is_all_day: draft.is_all_day,
    location: draft.location,
    notes: draft.notes,
    child: draft.child,
    source_type: sourceType,
    is_recurring: false,
    batch_id: batchId,
    // ⚠️ `?? null`, not `|| null`: 0 means "at the time of the event" and is a
    // real choice in the picker. See the header.
    reminder_minutes: reminderMinutes ?? null,
    status: "active" as const,
  }));

  const { data: importedRows, error } = await (supabase.from as any)("imported_events")
    .insert(rows)
    .select("id");
  if (error) return { insertedIds: [], error, mirrorError: null };

  let mirrorError: unknown = null;
  if (Array.isArray(importedRows) && importedRows.length > 0) {
    const eventRows = rows.map((row) => ({
      ...row,
      source_table: "imported_events",
      source_id: row.id,
      // #1637 — a saved calendar event is never a task. Without this the TBD
      // time was read as "task" by inferCategory and 13 of the 16 rows of a
      // daycare calendar landed in her to-do list.
      category: "event",
    }));
    // Await + check: `events` is the read path since Phase D (#82), so a
    // silent failure here means the event is GONE from her week while the
    // toast still says it saved (#207). Surface it — never swallow.
    const { error: evtErr } = await (supabase.from as any)("events").insert(eventRows);
    if (evtErr) {
      mirrorError = evtErr;
      reportError("[kinEventWrite] events dual-write failed", evtErr, {
        tags: { area: "events-dual-write", writer: "writeKinEvents" },
        extra: { source_type: sourceType, batch_id: batchId, count: eventRows.length },
      });
    }
  }

  return { insertedIds: rows.map((r) => r.id), error: null, mirrorError };
}
